import { ActionIcon, Badge, Button, Group, Image, Stack } from "@mantine/core";
import { Text } from "../Generics/Text";
import { IconCopy, IconTrash } from "@tabler/icons-react";
import React, { FC, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ProjectDataType, ShipDataType } from "@/types/apiTypes";
import { vesselImageUrls } from "@/data/vesselImageUrls";
import { UserContext } from "@/contexts/UserContext/UserContext";
import { useHomePageContext } from "@/hooks/contextHooks/useHomePageContext";
import { useFetchShips } from "@/hooks/apiHooks/useFetchShips";
import { useRemoveShipFromProject } from "@/hooks/apiHooks/useRemoveShipFromProject";
import { useInvalidateQueries } from "@/hooks/helperHooks/useInvalidateQueries";
import { usePageTransition } from "@/hooks/helperHooks/usePageTransition";
import { useAppColors } from "@/hooks/contextHooks/useAppColors";
import styles from "../../css/gen.module.css";

type Props = {
  setDeleteShipId: (id: string) => void;
  selectedProject: ProjectDataType | null;
  setIsDeleteModalOpen: (val: boolean) => void;
  confirmDeleteShip: (id: string) => void;
  shipIsInProject: (
    ship: ShipDataType,
    selectedProject: ProjectDataType,
  ) => boolean | undefined;
  handleShipProjectAssociation: (ship: any) => void;
  setIsAssociatedStudiesModalOpen: (val: boolean) => void;
};

export const GenShips: FC<Props> = ({
  setDeleteShipId,
  selectedProject,
  setIsDeleteModalOpen,
  confirmDeleteShip,
  shipIsInProject,
  handleShipProjectAssociation,
  setIsAssociatedStudiesModalOpen,
}) => {
  const transition = usePageTransition();

  const navigate = useNavigate();
  const context = useContext(UserContext);

  const { colors, isDarkMode } = useAppColors();

  const { invalidateShips } = useInvalidateQueries();

  const [shipSearchTerm, setShipSearchTerm] = useState<string>("");

  const { ships, setShips } = useHomePageContext();

  const [currentShipData, setCurrentShipData] = useState<ShipDataType[]>(ships);

  const handleCardClick = (ship: ShipDataType) => {
    navigate("/ship-details", { state: { shipData: ship, shipId: ship.id } });
  };

  const {
    data: shipsData,
    isLoading,
    //isSuccess,
    //isError,
  } = useFetchShips(shipSearchTerm, selectedProject?.id ?? null);

  const { mutate: removeShipFromProject } = useRemoveShipFromProject();

  // Array of ship data to be filtered without needing re-fetch

  useEffect(() => {
    if (ships) {
      if (shipSearchTerm) {
        setCurrentShipData(
          ships.filter((ship) =>
            ship.ship_name.toLowerCase().includes(shipSearchTerm.toLowerCase()),
          ),
        );
      } else {
        setCurrentShipData(ships);
      }
    }
  }, [ships, shipSearchTerm]);

  useEffect(() => {
    if (shipsData) {
      setShips(shipsData);
    }
  }, [shipsData, setShips]);

  console.log("🚢 SHIPDATA -> " + JSON.stringify(shipsData));

  useEffect(() => {
    invalidateShips();
  }, [selectedProject]);

  const handleCloneShip = async (shipId: string) => {
    try {
      await context?.cloneShip(shipId);
      // Fetch updated ships
      invalidateShips();
      /*
      notifications.show({
        title: "Notification cloned successfully!!",
        message: "Ship cloned successfully!!",
        color: "success",
        autoClose: 4000,
        position: "top-center",
      });
       */
    } catch (error) {
      console.error("Error cloning ship:", error);
    }
  };

  const handleDeleteShip = (ship: ShipDataType) => {
    if (!shipsData || !ship.id) return;

    const targetShip = shipsData.find((s) => s.id === ship.id);

    if (!targetShip) return;

    // If it has associated studies, show that modal and exit
    if (targetShip.studies?.length) {
      setIsAssociatedStudiesModalOpen(true);
      return; // 🚫 prevent further modal logic
    }

    setDeleteShipId(ship.id);

    if (ship.locked) {
      setIsDeleteModalOpen(true);
      // setIsAssociatedStudiesModalOpen(true);
    } else {
      setIsDeleteModalOpen(true);
    }
  };

  const handleRemoveShipFromProject = (ship: ShipDataType) => {
    if (!selectedProject) return;
    removeShipFromProject({ ship, projectId: selectedProject.id });
  };

  if (isLoading || transition) return null;

  return (
    <div
      className={styles.container}
      style={{
        backgroundColor: colors.gray,
        borderColor: colors.border,
      }}
    >
      <div className={styles.boxHorizontal}>
        <h3 style={{ color: colors.secondary }} className={styles.title}>
          Ships
        </h3>
        <input
          placeholder="Search ships..."
          value={shipSearchTerm}
          onChange={(e) => setShipSearchTerm(e.currentTarget.value)}
          className={styles.input}
          // styles={formStyles}
        />
        <div
          style={{
            backgroundColor: isDarkMode ? colors.secondary : colors.brand,
          }}
          className={styles.btn}
          onClick={() =>
            navigate("/add-ship-generic", {
              state: { projectId: selectedProject?.id },
            })
          }
        >
          <Text size="small" weight="bold" color={colors.primary}>
            + Add new ship
          </Text>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "flex-start",
          marginTop: "1rem",
        }}
      >
        {currentShipData.length === 0 ? (
          <Text color={colors.secondary}>No ship found</Text>
        ) : (
          <div className={styles.cardContainer}>
            {currentShipData.map((ship: any) => {
              return (
                <div key={ship.id}>
                  <div
                    className={styles.card}
                    style={{
                      borderColor: colors.border,
                    }}
                  >
                    <div onClick={() => handleCardClick(ship)}>
                      <Image
                        src={
                          vesselImageUrls[ship.vessel_type] ||
                          "https://via.placeholder.com/300"
                        }
                        alt={ship.vessel_type}
                        height={160}
                        style={{ borderRadius: 6 }}
                      />
                      {ship.locked && (
                        <Badge
                          color={colors.red}
                          style={{
                            position: "absolute",
                            top: "2rem",
                            right: "2rem",
                            zIndex: 10,
                          }}
                        >
                          LOCKED
                        </Badge>
                      )}
                    </div>

                    <Group mt="md" mb="xs">
                      <Text color={colors.secondary}>{ship.ship_name}</Text>
                      {/* {ship.locked && <Badge color="red">LOCKED</Badge>} */}
                    </Group>
                    <Text size="small" color={colors.secondary}>
                      Load Case: {ship.load_case_name}
                    </Text>
                    <Text size="small" color={colors.secondary}>
                      Type: {ship.vessel_type}
                    </Text>
                    <Text size="small" color={colors.secondary}>
                      DWT: {ship.vessel_size}
                    </Text>

                    <Stack mt="md">
                      {/* Conditionally render "Remove from Project" only if the ship is in the selected project */}

                      <Button
                        variant="outline"
                        color="black"
                        onClick={() =>
                          selectedProject
                            ? handleRemoveShipFromProject(ship)
                            : handleShipProjectAssociation(ship)
                        }
                        style={{
                          backgroundColor: isDarkMode
                            ? colors.secondary
                            : selectedProject
                              ? colors.red
                              : colors.brand,
                          borderRadius: 5,
                          borderColor: selectedProject
                            ? colors.red
                            : colors.border,
                        }}
                      >
                        <Text weight="bold" size="small" color={colors.primary}>
                          {selectedProject
                            ? "Remove from Project"
                            : "Add to Project"}
                        </Text>
                      </Button>

                      <Group justify="space-between">
                        <Button
                          variant="light"
                          color="blue"
                          onClick={() => handleCloneShip(ship.id)}
                          leftSection={
                            <IconCopy size={16} color={colors.secondary} />
                          }
                          style={{
                            backgroundColor: colors.selected,
                            borderColor: colors.border,
                          }}
                        >
                          <Text size="small" color={colors.secondary}>
                            Clone
                          </Text>
                        </Button>
                        <ActionIcon
                          variant="subtle"
                          size="lg"
                          color={colors.red}
                          onClick={() => handleDeleteShip(ship)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Stack>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
